import "./index.css";
import { useEffect, useState } from "react";
import FeedItem from "../feed-item";
import EmptyState from "../empty-state";
import LoadingState from "../loading-state";
import Button from "../button";
import CrawlCountdown from "../crawl-countdown";
import { downloadCsvBlob, rowsToCsv } from "../../functions/csvExport";

function FeedHolder({
  feedItems,
  isLoaded,
  inputLength,
  openSiteAdder,
}) {
  const [renderedFeedItems, setFeedItems] = useState([]);

  const BATCH_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);

  useEffect(() => {
    setFeedItems(feedItems || []);
  }, [feedItems]);

  // Infinite scroll (feed only)
  useEffect(() => {
    const handleScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 200;

      if (nearBottom && visibleCount < (feedItems?.length || 0)) {
        setVisibleCount((prev) =>
          Math.min(prev + BATCH_SIZE, feedItems.length)
        );
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [visibleCount, feedItems?.length]);

  const visibleFeedItems = renderedFeedItems.slice(0, visibleCount);

  // Feed site filter
  const [filterForFeed, setFilterForFeed] = useState(null);

  const uniqueSites = [
    ...new Set(
      (renderedFeedItems || [])
        .map((i) => i.site)
        .filter(Boolean)
        .map((s) => s.trim().toLowerCase())
    ),
  ];

    const changeFilter = (site) => {
        const selectedSite = site.innerText.trim();
        if (selectedSite === filterForFeed) {
            site.classList.remove("selected-filter");
            setFilterForFeed(null);
            setFeedItems(feedItems);
        } else {
            const filteredFeed = renderedFeedItems.filter(
                (item) => item.site === selectedSite
            );
            setFilterForFeed(selectedSite);
            document
                .querySelectorAll(".filter-for-feed .filter-item")
                .forEach((el) => el.classList.remove("selected-filter"));
            site.classList.add("selected-filter");
            setFeedItems(filteredFeed);
        }
    };

  const downloadToCsv = () => {
    if (!renderedFeedItems.length) return;

    const excludedFields = ["hasBeenSeen", "tag", "placeholder"];
    const header = Object.keys(renderedFeedItems[0]).filter(
      (key) => !excludedFields.includes(key)
    );

    const rows = [
      header,
      ...renderedFeedItems.map((row) => header.map((field) => row[field])),
    ];
    const csv = rowsToCsv(rows);
    downloadCsvBlob(
      `mako-data-${new Date().toISOString().split("T")[0]}.csv`,
      csv
    );
  };

  // ---------- Build feedHTML ----------
  let feedHTML;

  if (!isLoaded) {
    feedHTML = <LoadingState />;
  } else if (!renderedFeedItems.length) {
    const message =
      inputLength === 0
        ? "Add sites to generate your feed..."
        : "We're monitoring your sites for posts...";

    feedHTML = <EmptyState message={message} openSiteAdder={openSiteAdder} />;
  } else {
    let columnA = [];
    let columnB = [];
    let columnC = [];
    let mobileColumn = [];
    let ticker = 0;

    for (let i = 0; i < visibleFeedItems.length; i++) {
      const item = visibleFeedItems[i];

      const tile = (
        <FeedItem
          key={`${item.url || item.title || "item"}-${i}`}
          date={item.date}
          tag={item.tag}
          image={item.image}
          desc={item.excerpt}
          insight={item.insight}
          hasBeenSeen={item.hasBeenSeen}
          site={item.site}
          title={item.title}
          index={i}
          url={item.url}
          placeholder={item.placeholder}
          wordCount={item.wordCount}
          targetKeyword={item.targetKeyword}
          aiGeneratedScore={item.aiGeneratedScore}
        />
      );

      mobileColumn.push(tile);

      switch (ticker) {
        case 0:
          columnA.push(tile);
          ticker++;
          break;
        case 1:
          columnB.push(tile);
          ticker++;
          break;
        case 2:
          columnC.push(tile);
          ticker = 0;
          break;
        default:
          columnA.push(tile);
          ticker = 1;
      }
    }

    const documentWidth = document.body.clientWidth;
    feedHTML =
      documentWidth >= 800 ? (
        <>
          <div className="tile-column">{columnA}</div>
          <div className="tile-column">{columnB}</div>
          <div className="tile-column">{columnC}</div>
        </>
      ) : (
        <div className="tile-column">{mobileColumn}</div>
      );
  }

  return (
    <>
      <div id="download-holder">
        <CrawlCountdown />
        <Button
          text="Download CSV"
          image="../images/download.png"
          addedFunction={downloadToCsv}
        />
      </div>

      <div className="filter-for-feed">
        {uniqueSites.map((site) => (
          <div
            key={site}
            className="filter-item tiny-text"
            onClick={(e) => changeFilter(e.currentTarget)}
          >
            {site}
          </div>
        ))}
      </div>

      <div className="feed-holder">{feedHTML}</div>
    </>
  );
}

export default FeedHolder;
